import type { MissSound, ShotHooks } from '@/lib/uploader/engine'
import type { RowView, UploadState } from '@/components/uploader/upload-row'

import type {
    ChangeEvent,
    CSSProperties,
    DragEvent,
    FC,
    KeyboardEvent,
    PointerEvent as ReactPointerEvent,
    ReactNode
} from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HoopEngine } from '@/lib/uploader/engine'
import { playAirball, playBrick, playLebron, playSwish, unlockSound } from '@/lib/uploader/sound'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { FileGlyph } from '@/components/uploader/file-glyph'
import { HoopBack, HoopFront } from '@/components/uploader/hoop'
import { UploadBadge } from '@/components/uploader/upload-badge'
import { UploadRow } from '@/components/uploader/upload-row'

import {
    MAX_UPLOAD_BYTES,
    TAG_COLORS,
    describeFile,
    formatSize,
    shortenName,
    uploadFile
} from '@/lib/uploader/files'

import { FILE_SIZE, PANEL, REST } from '@/lib/uploader/geometry'

interface Item {
    id: string
    name: string
    bytes: number
    tag: string
    color: string
    demo: boolean
    file: File | null
    tooLarge: boolean
    progress: number
    status: UploadState['status']
    counted: boolean
    epoch: number
    settled: boolean
}

interface ShotView {
    id: string
    tag: string
    color: string
}

interface LoadedView extends ShotView {
    name: string
}

const DEMO_NAME = "final_final_v7.pdf"
const DEMO_BYTES = Math.round(2.4 * 1024 * 1024)
const RETURN_DELAY_MS = 150
const WASH_MS = 750
const PARALLEL_UPLOADS = 2
const VISIBLE_ROWS = 3
const KEPT_ROWS = 8

const isValid = (item: Item): boolean => !item.tooLarge && item.status !== 'failed'

const hasFiles = (event: DragEvent<HTMLElement>): boolean => Array.from(event.dataTransfer?.types ?? []).includes('Files')

/*
 * Nothing but net. A 540 x 675 panel drawn at a fixed design size and scaled as a whole,
 * so every measurement, the hoop and the physics stay in exact proportion on any screen.
 * React lays it out once; HoopEngine moves things; this component keeps score.
 */
const NothingButNet: FC = (): ReactNode => {
    const frameRef = useRef<HTMLDivElement>(null)
    const demoRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const backboardRef = useRef<SVGGElement>(null)
    const rimBackRef = useRef<SVGGElement>(null)
    const rimFrontRef = useRef<SVGGElement>(null)
    const netRefs = useRef<Array<SVGPathElement | null>>([])
    const hoopBackLayerRef = useRef<HTMLDivElement>(null)
    const hoopFrontLayerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const engineRef = useRef<HoopEngine | null>(null)

    const itemsRef = useRef(new Map<string, Item>())
    const rowsRef = useRef<Array<RowView>>([])
    const queueRef = useRef<Array<Item>>([])
    const loadedRef = useRef<Array<Item>>([])
    const activeUploadsRef = useRef(0)
    const pendingShotsRef = useRef(0)
    const epochRef = useRef(0)
    const idRef = useRef(0)
    const timersRef = useRef(new Set<number>())
    const returnTimerRef = useRef<number | null>(null)
    const washTimerRef = useRef<number | null>(null)
    const dragTimerRef = useRef<number | null>(null)
    const dragDepthRef = useRef(0)
    const lastReleaseRef = useRef(0)
    const reducedRef = useRef(false)
    const streakRef = useRef(0)

    const [scale, setScale] = useState<number | null>(null)
    const [count, setCount] = useState(0)
    const [rows, setRows] = useState<Array<RowView>>([])
    const [shots, setShots] = useState<Array<ShotView>>([])
    const [loaded, setLoaded] = useState<LoadedView | null>(null)
    const [plusOnes, setPlusOnes] = useState<Array<string>>([])
    const [washing, setWashing] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const [announcement, setAnnouncement] = useState('')
    const reduced = usePrefersReducedMotion()

    // Everything below reads refs and state setters only, so no handler ever sees stale state.

    const nextId = (prefix: string): string => {
        idRef.current += 1

        return `${prefix}-${idRef.current}`
    }

    const later = (run: () => void, delay: number): void => {
        const timer = window.setTimeout(() => {
            timersRef.current.delete(timer)
            run()
        }, delay)

        timersRef.current.add(timer)
    }

    const announce = (message: string): void => {
        setAnnouncement('')
        later(() => setAnnouncement(message), 60)
    }

    const updateRows = (next: Array<RowView>): void => {
        rowsRef.current = next
        setRows(next)
    }

    const createItem = (name: string, bytes: number, type: string, file: File | null, demo: boolean): Item => {
        const look = describeFile(name, type)
        const tooLarge = !demo && bytes > MAX_UPLOAD_BYTES

        const item: Item = {
            id: nextId(demo ? 'demo' : 'file'),
            name,
            bytes,
            tag: look.tag,
            color: look.color,
            demo,
            file,
            tooLarge,
            progress: demo ? 1 : 0,
            status: demo ? 'done' : tooLarge ? 'failed' : 'waiting',
            counted: false,
            epoch: epochRef.current,
            settled: false
        }

        itemsRef.current.set(item.id, item)

        return item
    }

    const cancelDemoReturn = (): void => {
        if (returnTimerRef.current === null) return

        window.clearTimeout(returnTimerRef.current)
        returnTimerRef.current = null
    }

    // The next file pops into hand right after a shot lands, without waiting for the upload.
    const scheduleDemoReturn = (): void => {
        if (pendingShotsRef.current > 0) return

        cancelDemoReturn()
        returnTimerRef.current = window.setTimeout(() => {
            returnTimerRef.current = null

            if (pendingShotsRef.current !== 0) return

            showLoaded()
            engineRef.current?.returnDemo()
        }, RETURN_DELAY_MS)
    }

    const wash = (): void => {
        setWashing(true)

        if (washTimerRef.current !== null) window.clearTimeout(washTimerRef.current)

        washTimerRef.current = window.setTimeout(() => {
            washTimerRef.current = null
            setWashing(false)
        }, WASH_MS)
    }

    const plusOne = (): void => {
        const id = nextId('plus')

        setPlusOnes(list => [...list, id])
        later(() => setPlusOnes(list => list.filter(entry => entry !== id)), 760)
    }

    const hooksFor = (item: Item): ShotHooks => ({
        onEntry: () => {
            if (!isValid(item)) return

            wash()
            plusOne()
            playSwish()

            // Every two makes in a row earn the LeBron call, then the count starts again.
            streakRef.current += 1

            if (streakRef.current >= 2) {
                streakRef.current = 0
                playLebron()
            }
        },
        onScore: () => {
            if (!isValid(item)) return

            item.counted = true
            item.epoch = epochRef.current
            setCount(value => value + 1)
        },
        onRow: () => {
            const next = [...rowsRef.current, {
                id: item.id,
                name: shortenName(item.name),
                size: formatSize(item.bytes),
                tag: item.tag,
                color: item.color,
                leaving: false,
                leaveDelay: 0
            }]

            // Rows far up the stack are long gone from view, so they are let go entirely.
            const kept = next.slice(-KEPT_ROWS)

            next.slice(0, Math.max(0, next.length - KEPT_ROWS)).forEach(row => itemsRef.current.delete(row.id))
            updateRows(kept)
        },
        onDone: () => {
            pendingShotsRef.current = Math.max(0, pendingShotsRef.current - 1)

            if (!item.demo) setShots(list => list.filter(shot => shot.id !== item.id))

            scheduleDemoReturn()
        }
    })

    const pumpUploads = (): void => {
        while (activeUploadsRef.current < PARALLEL_UPLOADS && queueRef.current.length > 0) {
            const item = queueRef.current.shift()

            if (!item || !item.file) continue

            activeUploadsRef.current += 1
            item.status = 'uploading'

            uploadFile(item.file, fraction => {
                item.progress = Math.max(item.progress, fraction)
            })
                .then(() => {
                    item.progress = 1
                    item.status = 'done'
                })
                .catch(() => {
                    item.status = 'failed'

                    // A shot that already counted but never reached storage gives its point back.
                    if (item.counted && item.epoch === epochRef.current) {
                        item.counted = false
                        setCount(value => Math.max(0, value - 1))
                    }
                })
                .finally(() => {
                    item.file = null
                    activeUploadsRef.current -= 1
                    pumpUploads()
                })
        }
    }

    // The file in hand shows the next dropped file waiting to be shot, or the demo file when none wait.
    const showLoaded = (): void => {
        const next = loadedRef.current[0]

        setLoaded(next ? { id: next.id, name: next.name, tag: next.tag, color: next.color } : null)
    }

    // Dropped or picked files wait in hand, one at a time, until they are shot through the hoop.
    const addFiles = (files: Array<File>): void => {
        const engine = engineRef.current

        if (!engine || files.length === 0) return

        const wasEmpty = loadedRef.current.length === 0

        files.forEach(file => loadedRef.current.push(createItem(file.name, file.size, file.type, file, false)))

        if (wasEmpty && engine.isDemoReady()) showLoaded()
        if (!engine.isDemoReady() && pendingShotsRef.current === 0) {
            cancelDemoReturn()
            showLoaded()
            engine.returnDemo()
        }

        announce(files.length === 1 ? `${files[0].name} is ready. Take the shot to upload it.` : `${files.length} files are ready. Shoot each one to upload it.`)
    }

    const clearRows = (): void => {
        const live = rowsRef.current.filter(row => !row.leaving)

        if (live.length === 0) return

        const visible = live.slice(-2)

        updateRows(rowsRef.current.map(row => {
            const order = visible.indexOf(row)

            return { ...row, leaving: true, leaveDelay: order < 0 ? 0 : order * 90 }
        }))

        later(() => {
            updateRows(rowsRef.current.filter(row => !row.leaving))
            epochRef.current += 1
            setCount(0)
            scheduleDemoReturn()
        }, (visible.length - 1) * 90 + 480)
    }

    const pulseHoop = (): void => {
        if (reducedRef.current) return

        const keyframes = [{ transform: 'scale(1)' }, { transform: 'scale(1.035)', offset: 0.38 }, { transform: 'scale(1)' }]
        const timing = { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }

        hoopBackLayerRef.current?.animate(keyframes, timing)
        hoopFrontLayerRef.current?.animate(keyframes, timing)
    }

    const handlers = useRef({
        demoShot: (): ShotHooks => {
            throw new Error('Uploader is not ready.')
        },
        demoMiss: (_kind: MissSound): void => undefined,
        demoMissed: (): void => undefined
    })

    // Hitting the rim or the backboard is a brick; touching nothing at all is an airball.
    handlers.current.demoMiss = (kind: MissSound): void => {
        streakRef.current = 0

        if (kind === 'air') playAirball()
        else playBrick()
    }

    // A made shot uploads the file in hand: the next dropped file if one waits, otherwise the demo file.
    handlers.current.demoShot = (): ShotHooks => {
        cancelDemoReturn()

        const waiting = loadedRef.current.shift()
        const item = waiting ?? createItem(DEMO_NAME, DEMO_BYTES, 'application/pdf', null, true)

        if (waiting && !waiting.tooLarge) {
            queueRef.current.push(waiting)
            pumpUploads()
        }

        pendingShotsRef.current += 1

        return hooksFor(item)
    }

    handlers.current.demoMissed = (): void => {
        announce("Missed. The file is back in place for another shot.")
    }

    const readUpload = useCallback((id: string): UploadState => {
        const item = itemsRef.current.get(id)

        return item ? { progress: item.progress, status: item.status } : { progress: 0, status: 'failed' }
    }, [])

    const settleRef = useRef<(id: string, ok: boolean) => void>(() => undefined)

    settleRef.current = (id: string, ok: boolean): void => {
        const item = itemsRef.current.get(id)

        if (!item || item.settled) return

        item.settled = true
        announce(ok ? `${item.name} uploaded.` : `${item.name} failed to upload.`)
        scheduleDemoReturn()
    }

    const settleRow = useCallback((id: string, ok: boolean) => settleRef.current(id, ok), [])

    useEffect(() => {
        const frame = frameRef.current
        const demo = demoRef.current
        const canvas = canvasRef.current
        const backboard = backboardRef.current
        const rimBack = rimBackRef.current
        const rimFront = rimFrontRef.current

        if (!frame || !demo || !canvas || !backboard || !rimBack || !rimFront) return

        const engine = new HoopEngine(
            { frame, demo, canvas, net: netRefs.current, backboard, rimBack, rimFront },
            {
                demoShot: () => handlers.current.demoShot(),
                demoMiss: kind => handlers.current.demoMiss(kind),
                demoMissed: () => handlers.current.demoMissed()
            }
        )

        engineRef.current = engine

        return () => {
            engine.destroy()
            engineRef.current = null

            // Anything in flight belonged to this engine; a fresh one starts from a clean court.
            pendingShotsRef.current = 0
            setShots([])
        }
    }, [])

    useEffect(() => {
        const frame = frameRef.current

        if (!frame) return

        const measure = (): void => {
            const width = frame.getBoundingClientRect().width

            if (width > 0) setScale(width / PANEL.width)
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(frame)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (scale !== null) engineRef.current?.setScale(scale)
    }, [scale])

    useEffect(() => {
        reducedRef.current = reduced
        engineRef.current?.setReducedMotion(reduced)
    }, [reduced])

    useEffect(() => {
        engineRef.current?.setRowCount(rows.filter(row => !row.leaving).length)
    }, [rows])

    // Files dropped beside the panel would otherwise make the browser open them and leave the page.
    useEffect(() => {
        const guard = (event: globalThis.DragEvent): void => {
            if (!event.dataTransfer || !Array.from(event.dataTransfer.types).includes('Files')) return
            if (frameRef.current?.contains(event.target as Node)) return

            event.preventDefault()

            if (event.type === 'dragover') event.dataTransfer.dropEffect = 'none'
        }

        window.addEventListener('dragover', guard)
        window.addEventListener('drop', guard)

        return () => {
            window.removeEventListener('dragover', guard)
            window.removeEventListener('drop', guard)
        }
    }, [])

    useEffect(() => {
        const timers = timersRef.current

        return () => {
            timers.forEach(timer => window.clearTimeout(timer))
            timers.clear()

            for (const timer of [returnTimerRef.current, washTimerRef.current, dragTimerRef.current]) {
                if (timer !== null) window.clearTimeout(timer)
            }
        }
    }, [])

    const endDragOver = (): void => {
        dragDepthRef.current = 0
        setDragOver(false)

        if (dragTimerRef.current === null) return

        window.clearTimeout(dragTimerRef.current)
        dragTimerRef.current = null
    }

    // A safety net for drags cancelled with Escape, which never send a final leave.
    const refreshDragTimer = (): void => {
        if (dragTimerRef.current !== null) window.clearTimeout(dragTimerRef.current)

        dragTimerRef.current = window.setTimeout(endDragOver, 900)
    }

    const onDragEnter = (event: DragEvent<HTMLDivElement>): void => {
        if (!hasFiles(event)) return

        event.preventDefault()
        dragDepthRef.current += 1

        if (dragDepthRef.current === 1) {
            setDragOver(true)
            pulseHoop()
        }

        refreshDragTimer()
    }

    const onDragOver = (event: DragEvent<HTMLDivElement>): void => {
        if (!hasFiles(event)) return

        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'

        if (dragDepthRef.current === 0) {
            dragDepthRef.current = 1
            setDragOver(true)
            pulseHoop()
        }

        refreshDragTimer()
    }

    const onDragLeave = (event: DragEvent<HTMLDivElement>): void => {
        if (!hasFiles(event)) return

        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)

        if (dragDepthRef.current === 0) endDragOver()
    }

    const onDrop = (event: DragEvent<HTMLDivElement>): void => {
        if (!hasFiles(event)) return

        event.preventDefault()
        endDragOver()
        unlockSound()

        addFiles(Array.from(event.dataTransfer.files))
    }

    const openPicker = (): void => {
        if (performance.now() - lastReleaseRef.current < 300) return

        unlockSound()
        inputRef.current?.click()
    }

    const onZoneKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        openPicker()
    }

    const onPick = (event: ChangeEvent<HTMLInputElement>): void => {
        const files = Array.from(event.target.files ?? [])

        event.target.value = ''
        addFiles(files)
    }

    const onDemoPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
        const engine = engineRef.current

        unlockSound()

        if (!engine || !engine.grab(event.nativeEvent)) return

        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onDemoPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
        engineRef.current?.drag(event.nativeEvent)
    }

    const onDemoPointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
        unlockSound()
        lastReleaseRef.current = performance.now()
        engineRef.current?.release(event.nativeEvent)

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const onDemoPointerCancel = (event: ReactPointerEvent<HTMLDivElement>): void => {
        lastReleaseRef.current = performance.now()
        engineRef.current?.release(event.nativeEvent, true)
    }

    const onDemoLostCapture = (): void => {
        engineRef.current?.release(null, true)
    }

    const onDemoKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        unlockSound()
        engineRef.current?.shootDemo()
    }

    const lit = washing || dragOver
    const ready = scale !== null
    const hasRows = rows.some(row => !row.leaving)
    const visibleRows = rows.slice(-VISIBLE_ROWS)

    return (
        <div
            ref={frameRef}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={cn(
                'relative aspect-[4/5] w-[min(540px,calc(100vw_-_32px),calc((100dvh_-_32px)_*_0.8))] shrink-0',
                scale === null && 'overflow-clip'
            )}
        >
            <div
                className={cn(
                    'absolute left-0 top-0 h-[675px] w-[540px] origin-top-left rounded-[28px] bg-sheet shadow-[inset_0_0_0_1px_rgba(255,255,255,0.55),0_0_0_1px_rgba(17,20,24,0.035),0_1px_2px_rgba(17,20,24,0.05),0_20px_44px_-30px_rgba(17,20,24,0.3)] transition-opacity duration-500 ease-out',
                    ready ? 'nbn-intro-panel opacity-100' : 'opacity-0'
                )}
                style={{ transform: `scale(${scale ?? 1})`, '--s': scale ?? 1 } as CSSProperties}
            >
                <header className={cn('absolute left-[28px] right-[28px] top-[28px] z-[7] flex items-start justify-between gap-4', ready && 'nbn-intro-header')}>
                    <div className='min-w-0'>
                        <h1 className='font-heading text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink'>
                            Upload files
                        </h1>

                        <p className='mt-1 text-[length:max(13px,calc(8.5px/var(--s,1)))] leading-[1.4] text-graphite'>
                            <span className='[@media(pointer:coarse)]:hidden'>Drag and drop, or take the shot.</span>
                            <span className='hidden [@media(pointer:coarse)]:inline'>Pick a file, then take the shot.</span>
                        </p>
                    </div>

                    <UploadBadge count={count} reduced={reduced} />
                </header>

                <div
                    role='button'
                    tabIndex={0}
                    aria-label='Upload files. Drop files here, or press Enter to choose them.'
                    onClick={openPicker}
                    onKeyDown={onZoneKeyDown}
                    className={cn(
                        'group/zone absolute left-[28px] top-[106px] z-[1] h-[270px] w-[484px] cursor-pointer rounded-[20px] shadow-[0_1px_2px_rgba(17,20,24,0.04),0_12px_28px_-20px_rgba(17,20,24,0.22)] outline-none transition-[background-color] ease-out focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-4 focus-visible:ring-offset-sheet',
                        lit ? 'bg-peach duration-150' : 'bg-white duration-[400ms]',
                        ready && 'nbn-intro-zone'
                    )}
                >
                    <svg aria-hidden='true' className='pointer-events-none absolute inset-0 size-full overflow-visible'>
                        <rect
                            x={0.75}
                            y={0.75}
                            width={482.5}
                            height={268.5}
                            rx={19.25}
                            pathLength={1470}
                            fill='none'
                            strokeWidth={1.5}
                            strokeDasharray='6 8'
                            className={cn(
                                'transition-[stroke] ease-out',
                                lit ? 'stroke-hoop duration-150' : 'stroke-dash duration-[400ms] group-hover/zone:stroke-rule'
                            )}
                        />
                    </svg>

                    <div className='flex flex-col items-center pt-[22px] text-center'>
                        <Upload aria-hidden='true' className='size-[18px] text-ink-soft' strokeWidth={1.5} />

                        <p className='mt-2.5 text-[length:max(15px,calc(9.5px/var(--s,1)))] font-medium leading-[1.3] text-ink'>
                            <span className='[@media(pointer:coarse)]:hidden'>Drop files here</span>
                            <span className='hidden [@media(pointer:coarse)]:inline'>Tap to choose files</span>
                        </p>

                        <p className='mt-0.5 text-[length:max(11px,calc(8.5px/var(--s,1)))] leading-[1.35] text-fog'>
                            or take the shot
                        </p>
                    </div>
                </div>

                <div ref={hoopBackLayerRef} className={cn('pointer-events-none absolute inset-0 z-[2] origin-[270px_330px]', ready && 'nbn-intro-hoop')}>
                    <HoopBack backboardRef={backboardRef} rimRef={rimBackRef} netRefs={netRefs} ready={ready} />
                </div>

                <canvas
                    ref={canvasRef}
                    aria-hidden='true'
                    className='pointer-events-none absolute left-[-60px] top-[-160px] z-[3] h-[875px] w-[660px]'
                />

                <div ref={hoopFrontLayerRef} className={cn('pointer-events-none absolute inset-0 z-[5] origin-[270px_330px]', ready && 'nbn-intro-hoop')}>
                    <HoopFront rimRef={rimFrontRef} />
                </div>

                {plusOnes.map(id => (
                    <span
                        key={id}
                        aria-hidden='true'
                        className='nbn-plus-one pointer-events-none absolute left-[322px] top-[352px] z-[6] text-[14px] font-semibold leading-none text-hoop'
                    >
                        +1
                    </span>
                ))}

                <div className='pointer-events-none absolute inset-x-0 bottom-0 top-[380px] z-[8] overflow-hidden rounded-b-[28px]'>
                    {visibleRows.map((row, index) => (
                        <UploadRow
                            key={row.id}
                            row={row}
                            slot={visibleRows.length - 1 - index}
                            read={readUpload}
                            onSettled={settleRow}
                        />
                    ))}
                </div>

                <button
                    type='button'
                    onClick={clearRows}
                    tabIndex={hasRows ? 0 : -1}
                    aria-hidden={!hasRows}
                    className={cn(
                        'absolute right-[22px] top-[647px] z-[9] rounded-[6px] px-1.5 py-1 text-[length:max(12px,calc(8.5px/var(--s,1)))] leading-none text-fog outline-none transition-[color,opacity] duration-300 ease-out before:absolute before:-inset-2 before:content-[""] hover:text-graphite focus-visible:ring-2 focus-visible:ring-ink',
                        hasRows ? 'opacity-100' : 'pointer-events-none opacity-0'
                    )}
                >
                    Clear
                </button>

                {shots.map(shot => (
                    <div
                        key={shot.id}
                        ref={element => engineRef.current?.attach(shot.id, element)}
                        aria-hidden='true'
                        className='pointer-events-none absolute left-0 top-0 z-[10] h-[72px] w-[56px] opacity-0 will-change-transform'
                    >
                        <div className='absolute inset-0 rounded-[6px] shadow-[0_10px_18px_-8px_rgba(17,20,24,0.26),0_2px_5px_-2px_rgba(17,20,24,0.1)]' />
                        <FileGlyph tag={shot.tag} color={shot.color} />
                    </div>
                ))}

                <div
                    ref={demoRef}
                    data-state='rest'
                    role='button'
                    tabIndex={0}
                    aria-label={`Shoot ${loaded?.name ?? DEMO_NAME} into the hoop`}
                    onPointerDown={onDemoPointerDown}
                    onPointerMove={onDemoPointerMove}
                    onPointerUp={onDemoPointerUp}
                    onPointerCancel={onDemoPointerCancel}
                    onLostPointerCapture={onDemoLostCapture}
                    onKeyDown={onDemoKeyDown}
                    onDragStart={event => event.preventDefault()}
                    className={cn('nbn-file absolute left-0 top-0 z-[10] h-[72px] w-[56px] rounded-[6px] outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-4 focus-visible:ring-offset-sheet', ready && 'nbn-intro-file')}
                    style={{ transform: `translate3d(${REST.x - FILE_SIZE.width / 2}px, ${REST.y - FILE_SIZE.height / 2}px, 0)` }}
                >
                    {/* A fingertip is bigger than a cursor, so on touch screens the file can be grabbed from a little around it. */}
                    <span aria-hidden='true' className='absolute -inset-2 rounded-[10px] [@media(pointer:coarse)]:-inset-7' />

                    <div className='nbn-file__tilt absolute inset-0'>
                        <div className='nbn-file__breath absolute inset-0'>
                            <div className='nbn-file__shadow absolute inset-0 rounded-[6px] shadow-[0_12px_20px_-8px_rgba(17,20,24,0.3),0_3px_6px_-2px_rgba(17,20,24,0.1)]' />
                        </div>

                        <div className='nbn-file__float absolute inset-0'>
                            <FileGlyph tag={loaded?.tag ?? 'PDF'} color={loaded?.color ?? TAG_COLORS.pdf} />
                        </div>
                    </div>

                    <span className='nbn-file__label pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap text-[length:max(10px,calc(8.5px/var(--s,1)))] leading-none text-graphite'>
                        {loaded ? shortenName(loaded.name) : DEMO_NAME}
                    </span>
                </div>

                <input
                    ref={inputRef}
                    type='file'
                    multiple
                    tabIndex={-1}
                    aria-hidden='true'
                    className='hidden'
                    onChange={onPick}
                />

                <p className='sr-only' aria-live='polite'>
                    {announcement}
                </p>
            </div>
        </div>
    )
}

export default NothingButNet
